import { useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, MessageBar, MessageBarBody, Textarea,
} from '@fluentui/react-components';
import { withdrawRelease, compareVersions, type Release, type ReleaseChange } from '../../api/releases';
import { releaseErrorMessage } from '../../releases/releaseChange';
import { useReleaseMutation } from './useReleases';

const MIN_REASON = 10;

export function WithdrawDialog({ productId, productName, release, releases, installedOn, onClose, onDone }: {
  productId: string;
  productName: string;
  release: Release;
  releases: Release[];
  installedOn: number | null;
  onClose: () => void;
  onDone: (r: ReleaseChange) => void;
}) {
  const [reason, setReason] = useState('');
  const m = useReleaseMutation(productId, (r: string) => withdrawRelease(productId, release.version, r));
  const isCurrent = release.status === 'Current';
  const fallback = isCurrent
    ? releases.filter((x) => x.status === 'Superseded').sort((a, b) => compareVersions(b.version, a.version))[0]
    : undefined;
  const blocked = isCurrent && !fallback;
  const valid = reason.trim().length >= MIN_REASON;

  return (
    <Dialog open onOpenChange={(_, d) => { if (!d.open && !m.isPending) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Withdraw {productName} {release.version}?</DialogTitle>
          <DialogContent>
            <p>
              It can no longer be installed. {installedOn ? `${installedOn} tenant${installedOn === 1 ? '' : 's'} still run it; they keep running it and show as "withdrawn build".` : ''}
            </p>
            {isCurrent && (
              <MessageBar intent={blocked ? 'error' : 'warning'}>
                <MessageBarBody>
                  {fallback ? `This is the current release. ${fallback.version} becomes current.` : 'No other release can become current. Publish another release first.'}
                </MessageBarBody>
              </MessageBar>
            )}
            <Field label="Reason (required)" hint={`Shown to anyone still running it. At least ${MIN_REASON} characters.`} style={{ marginTop: 12 }}>
              <Textarea value={reason} onChange={(_, d) => setReason(d.value)} rows={3} disabled={blocked || m.isPending} placeholder="e.g. Quiz answers graded wrongly. Fixed in 2.3.7.0." />
            </Field>
            {m.error && <MessageBar intent="error" style={{ marginTop: 12 }}><MessageBarBody>{releaseErrorMessage(m.error)}</MessageBarBody></MessageBar>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={m.isPending}>Cancel</Button>
            <Button appearance="primary" disabled={blocked || !valid || m.isPending} onClick={() => m.mutate(reason.trim(), { onSuccess: onDone })}>
              {m.isPending ? 'Withdrawing…' : `Withdraw ${release.version}`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
